import { CONSENT_GRANTED, resolveTrackingConsent } from "./consent-cookie";
import { track as vercelTrack } from '@vercel/analytics';
export { getAnalyticsPageContext } from "./analyticsContext";

// Server-side Vercel Analytics tracking
export const trackServerEvent = async (eventName, parameters = {}) => {
  try {
    // Only import server-side track in Node.js environment
    if (typeof window === "undefined") {
      const { track: vercelServerTrack } = await import('@vercel/analytics/server');
      const vercelEventData = Object.keys(parameters).length > 0 ? parameters : undefined;
      await vercelServerTrack(eventName, vercelEventData);
      
      if (DEBUG_MODE) {
        console.log("📈 Vercel Server Analytics Event:", eventName, vercelEventData);
      }
    }
  } catch (error) {
    if (DEBUG_MODE) {
      console.error("❌ Server Analytics Event Error:", error);
    }
  }
};

// GA4 Configuration
const configuredMeasurementId = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID || "G-F0R7E5FTJB";
export const GA4_MEASUREMENT_ID = /^G-[A-Z0-9]+$/.test(configuredMeasurementId) ? configuredMeasurementId : null;
const HAS_GA_ID = !!GA4_MEASUREMENT_ID;

// Debug logging is opt-in so local dev consoles stay usable.
const DEBUG_MODE = process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === "true";

const CONSENT_CONFIG = {
  granted: {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "granted",
    functionality_storage: "granted",
    security_storage: "granted",
  },
  denied: {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    security_storage: "granted",
  },
};

// Cookie consent helper - SSR safe.
// Every consent decision in the app funnels through here (GoogleAnalyticsProvider,
// AnalyticsTracker, event/page-view suppression, GA4 init), so honouring Global
// Privacy Control in one place covers all of them.
export const hasTrackingConsent = () => {
  // Check if we're in the browser
  if (typeof window === "undefined") {
    return false;
  }

  return resolveTrackingConsent() === CONSENT_GRANTED;
};

export function applyAnalyticsContext(context = null) {
  if (typeof window === "undefined") {
    return;
  }

  if (!context) {
    window.__analyticsContext = null;
    window.__analyticsSuppressed = true;
    return;
  }

  window.__analyticsContext = context;
  window.__analyticsSuppressed =
    !hasTrackingConsent() || !context.shouldTrack;
}

function getClientAnalyticsContext() {
  if (typeof window === "undefined") {
    return {};
  }

  return window.__analyticsContext?.defaultEventParameters || {};
}

function shouldSuppressAnalytics() {
  if (typeof window === "undefined") {
    return true;
  }

  return Boolean(window.__analyticsSuppressed) || !hasTrackingConsent();
}

// Log analytics events in debug mode
const debugLog = (eventType, eventName, parameters) => {
  if (DEBUG_MODE) {
    console.group(`📊 GA4 ${eventType}: ${eventName}`);
    console.log("Parameters:", parameters);
    console.log("Timestamp:", new Date().toISOString());
    console.log("Consent:", hasTrackingConsent() ? "✅ Granted" : "❌ Not granted");
    console.groupEnd();
  }
};

export const updateGAConsent = (status = hasTrackingConsent() ? "granted" : "denied") => {
  try {
    if (typeof window === "undefined") {
      return;
    }

    const consentStatus = status === "granted" ? "granted" : "denied";
    const consentSettings = CONSENT_CONFIG[consentStatus];

    if (!consentSettings) {
      return;
    }

    const gtag = typeof window.gtag === "function"
      ? window.gtag
      : (...args) => {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push(args);
        };

    gtag("consent", "update", consentSettings);

    if (DEBUG_MODE) {
      console.log(`🔄 GA4 consent updated: ${consentStatus}`);
    }
  } catch (error) {
    if (DEBUG_MODE) {
      console.error("❌ GA4 Consent Update Error:", error);
    }
  }
};

// Send event to GA4 and Vercel Analytics with error handling
// Note: We no longer block events based on consent. GA4's built-in consent
// mode handles this automatically — it sends cookieless pings when
// analytics_storage is 'denied' and full tracking when 'granted'.
export const sendGAEvent = (eventName, parameters = {}) => {
  try {
    if (shouldSuppressAnalytics()) {
      debugLog("Suppressed Event", eventName, parameters);
      return;
    }

    const contextParameters = getClientAnalyticsContext();
    const eventParameters = {
      ...contextParameters,
      ...parameters,
      debug_mode: DEBUG_MODE,
    };

    // Send to GA4 (consent mode handles what data is collected)
    if (HAS_GA_ID && typeof window !== "undefined" && window.gtag) {
      debugLog("Event", eventName, eventParameters);
      window.gtag("event", eventName, eventParameters);
    } else if (DEBUG_MODE && HAS_GA_ID) {
      console.warn("⚠️ GA4: gtag not available");
    } else if (DEBUG_MODE && !HAS_GA_ID) {
      console.warn("⚠️ GA4: No measurement ID configured");
    }

    // Send to Vercel Analytics (client-side)
    if (typeof window !== "undefined") {
      const vercelEventData =
        Object.keys(eventParameters).length > 0 ? eventParameters : undefined;
      vercelTrack(eventName, vercelEventData);
      
      if (DEBUG_MODE) {
        console.log("📈 Vercel Analytics Event:", eventName, vercelEventData);
      }
    }
  } catch (error) {
    if (DEBUG_MODE) {
      console.error("❌ Analytics Event Error:", error);
    }
  }
};

// Track page views with error handling
// Note: GA4 consent mode handles cookieless vs full tracking automatically
export const trackPageView = ({
  path = "",
  title = "",
  location = "",
  referrer = "",
  section = "",
  trafficType = null,
} = {}) => {
  try {
    if (shouldSuppressAnalytics()) {
      debugLog("Suppressed Page View", path || location, { page_title: title });
      return;
    }

    const pageParameters = {
      ...getClientAnalyticsContext(),
      ...(section ? { app_section: section } : {}),
      ...(path ? { page_path: path } : {}),
      ...(location ? { page_location: location } : {}),
      ...(referrer ? { page_referrer: referrer } : {}),
      ...(title ? { page_title: title } : {}),
      ...(trafficType ? { traffic_type: trafficType } : {}),
      debug_mode: DEBUG_MODE,
    };

    // Send to GA4 (consent mode handles what data is collected)
    if (HAS_GA_ID && typeof window !== "undefined" && window.gtag) {
      debugLog("Page View", path || location, pageParameters);
      window.gtag("event", "page_view", pageParameters);
    } else if (DEBUG_MODE && HAS_GA_ID) {
      console.warn("⚠️ GA4: gtag not available for page view");
    } else if (DEBUG_MODE && !HAS_GA_ID) {
      console.warn("⚠️ GA4: No measurement ID configured for page view");
    }

    // Vercel Analytics page views are already handled by the root <Analytics /> component.
  } catch (error) {
    if (DEBUG_MODE) {
      console.error("❌ Page View Error:", error);
    }
  }
};

// Set user properties with error handling
export const setUserProperties = (properties) => {
  try {
    if (shouldSuppressAnalytics()) {
      debugLog("User Properties Blocked", "set_user_properties", properties);
      return;
    }

    if (!HAS_GA_ID) {
      return;
    }

    if (typeof window === "undefined" || !window.gtag) {
      if (DEBUG_MODE) {
        console.warn("⚠️ GA4: gtag not available for user properties");
      }
      return;
    }

    debugLog("User Properties", "set_user_properties", properties);

    window.gtag("set", "user_properties", properties);
  } catch (error) {
    if (DEBUG_MODE) {
      console.error("❌ GA4 User Properties Error:", error);
    }
  }
};

// Set user ID for cross-device tracking with error handling
export const setUserId = (userId) => {
  try {
    if (shouldSuppressAnalytics()) {
      debugLog("User ID Blocked", "set_user_id", { user_id: userId });
      return;
    }

    if (!HAS_GA_ID) {
      return;
    }

    if (typeof window === "undefined" || !window.gtag) {
      if (DEBUG_MODE) {
        console.warn("⚠️ GA4: gtag not available for user ID");
      }
      return;
    }

    debugLog("User ID", "set_user_id", { user_id: userId });

    window.gtag("config", GA4_MEASUREMENT_ID, {
      user_id: userId,
      debug_mode: DEBUG_MODE,
    });
  } catch (error) {
    if (DEBUG_MODE) {
      console.error("❌ GA4 User ID Error:", error);
    }
  }
};

// Track custom events with enhanced parameters
export const trackEvent = (action, category, label, value, customParameters = {}) => {
  const parameters = {
    event_category: category,
    event_label: label,
    ...(value !== undefined && { value }),
    ...customParameters,
  };

  sendGAEvent(action, parameters);
};

// Predefined event tracking functions
export const analytics = {
  // User actions
  trackButtonClick: (buttonName, location, metadata = {}) => {
    sendGAEvent("button_click", {
      button_name: buttonName,
      button_location: location,
      ...metadata,
    });
  },

  trackFormSubmit: (formName, formLocation, metadata = {}) => {
    sendGAEvent("form_submit", {
      form_name: formName,
      form_location: formLocation,
      ...metadata,
    });
  },

  // Navigation
  trackLinkClick: (linkText, linkUrl, isExternal = false) => {
    sendGAEvent(isExternal ? "external_link_click" : "internal_link_click", {
      link_text: linkText,
      link_url: linkUrl,
    });
  },

  // User engagement
  trackScroll: (percentage) => {
    sendGAEvent("scroll", {
      percent_scrolled: percentage,
    });
  },

  trackSearch: (searchTerm, searchLocation) => {
    sendGAEvent("search", {
      search_term: searchTerm,
      search_location: searchLocation,
    });
  },

  // Video tracking
  trackVideoPlay: (videoTitle, videoUrl) => {
    sendGAEvent("video_play", {
      video_title: videoTitle,
      video_url: videoUrl,
    });
  },

  trackVideoProgress: (videoTitle, percentage) => {
    sendGAEvent("video_progress", {
      video_title: videoTitle,
      video_percent: percentage,
    });
  },

  // File downloads
  trackFileDownload: (fileName, fileType) => {
    sendGAEvent("file_download", {
      file_name: fileName,
      file_extension: fileType,
    });
  },

  // E-commerce/Conversion events
  trackSignUp: (method, pathname) => {
    sendGAEvent("sign_up", {
      method,
      location: pathname,
    });
  },

  trackLogin: (method) => {
    sendGAEvent("login", {
      method,
    });
  },

  trackPurchase: (transactionId, value, currency = "USD", items = []) => {
    sendGAEvent("purchase", {
      transaction_id: transactionId,
      value,
      currency,
      items,
    });
  },

  // Custom business events
  trackSimulationStart: (simulationType, persona) => {
    sendGAEvent("simulation_start", {
      simulation_type: simulationType,
      persona,
    });
  },

  trackSimulationComplete: (simulationType, score, duration) => {
    sendGAEvent("simulation_complete", {
      simulation_type: simulationType,
      score,
      duration_seconds: duration,
    });
  },

  trackFeedbackGenerated: (feedbackType, simulationId) => {
    sendGAEvent("feedback_generated", {
      feedback_type: feedbackType,
      simulation_id: simulationId,
    });
  },

  // Error tracking
  trackError: (errorMessage, errorLocation) => {
    sendGAEvent("exception", {
      description: errorMessage,
      fatal: false,
      error_location: errorLocation,
    });
  },
};

// Enhanced measurement configuration with error handling
export const configureEnhancedMeasurement = () => {
  try {
    if (
      shouldSuppressAnalytics() ||
      !HAS_GA_ID ||
      typeof window === "undefined" ||
      !window.gtag
    ) {
      return;
    }

    // Configure GA4 with valid options only
    window.gtag("config", GA4_MEASUREMENT_ID, {
      anonymize_ip: true,
      send_page_view: false,
      debug_mode: DEBUG_MODE,
    });

    // Set custom dimensions separately (valid approach)
    window.gtag("config", GA4_MEASUREMENT_ID, {
      custom_map: {
        dimension1: "user_type",
        dimension2: "workspace_id", 
        dimension3: "workspace_name",
      },
    });

    if (DEBUG_MODE) {
      console.log("✅ GA4 Enhanced Measurement configured");
    }
  } catch (error) {
    if (DEBUG_MODE) {
      console.error("❌ GA4 Enhanced Measurement Error:", error);
    }
  }
};

// Initialize GA4 with consent and error handling
export const initializeGA4 = () => {
  try {
    if (typeof window === "undefined") return;

    // Check if GA is configured
    if (!HAS_GA_ID) {
      if (DEBUG_MODE) {
        console.log("⚠️ GA4 initialization skipped - no measurement ID");
      }
      return;
    }

    // Wait for consent before initializing
    if (!hasTrackingConsent()) {
      if (DEBUG_MODE) {
        console.log("⏸️ GA4 initialization paused - waiting for consent");
      }
      return;
    }

    updateGAConsent("granted");

    if (typeof window.gtag === "function") {
      configureEnhancedMeasurement();
      window.GA_INITIALIZED = true;
      window.dispatchEvent(new Event("ga4-ready"));
    }

    if (DEBUG_MODE) {
      console.log("✅ GA4 initialized with measurement ID:", GA4_MEASUREMENT_ID);
    }
  } catch (error) {
    if (DEBUG_MODE) {
      console.error("❌ GA4 Initialization Error:", error);
    }
  }
};
